/* ======================================================================================================================
   HEADER / NAV — lane-owned script
   ----------------------------------------------------------------------------------------------------------------------
   Owned by ONE chat at a time together with sections/header.liquid, assets/section-header.css,
   snippets/header-sidebar.liquid and snippets/mega-menu.liquid. Nothing here belongs in assets/theme.js, which is a
   shared file no lane owns.
   ====================================================================================================================== */
(() => {
  'use strict';

  /* --------------------------------------------------------------------------------------------------------------------
     1. Drop the stray `open` attribute the theme leaves behind on a closed nav disclosure.
     --------------------------------------------------------------------------------------------------------------------
     Measured 2026-08-23: two seconds after the pointer left a mega-menu, the <details> reported
     aria-expanded="false" and its panel opacity 0, but `open` was still set. assets/section-header.css already stops
     that ghost sheet hit-testing; this also takes it out of the tab order and out of the accessibility tree, which
     CSS cannot do. Deliberately does NOT touch aria-expanded or run the animations — the theme still owns those.
     ------------------------------------------------------------------------------------------------------------------ */
  const SETTLE_MS = 600; // longer than the theme's 0.4s hide timeline

  const closeIfSettled = (details) => {
    if (details.getAttribute('aria-expanded') === 'true') return;
    if (!details.open) return;
    details.open = false;
  };

  const watch = (details) => {
    let timer;
    new MutationObserver(() => {
      if (details.getAttribute('aria-expanded') === 'true') {
        clearTimeout(timer);
        return;
      }
      clearTimeout(timer);
      timer = setTimeout(() => closeIfSettled(details), SETTLE_MS);
    }).observe(details, { attributes: true, attributeFilter: ['aria-expanded'] });
  };

  /* --------------------------------------------------------------------------------------------------------------------
     1c. TWO BEHAVIOURS SIDE BY SIDE — click menus and hover menus.
     --------------------------------------------------------------------------------------------------------------------
     Owner 2026-08-23, in Chrome:
       "shop all and sale ... when you hover on it, it should not open automatically. mouse should turn into hand and
        it should be opened on click and closed on click, or closed when mouse leaves menu area including dropped
        content"
       "for new in and signature styles — keep as it was before your changes — open on hover, mouse does not turn to
        hand"

     sections/header.liquid marks the first group with `data-open-on-click` on the <li> and gives only those a
     trigger="click"; everything else keeps the section's `menu_open_trigger` setting, which is "hover". So theme.js
     already does the right thing for New In / Signature Styles and for OPENING a click menu. Everything below is the
     three things it does not do.
     ------------------------------------------------------------------------------------------------------------------ */
  const CLOSE_DELAY = 250; // the same tolerance theme.js uses for hover-out

  const CLICK_ITEM = '.header__primary-nav-item[data-open-on-click]';
  const CLICK_LEVEL = CLICK_ITEM + ' > details.header__menu-disclosure';
  const ANY_TOP_LEVEL = '.header__primary-nav-item > details.header__menu-disclosure';

  const deviceCanHover = () => window.matchMedia('(hover: hover)').matches;

  /* toggle() only exists once <details is="..."> has upgraded. Fall back to the attributes it would have written,
     so a browser without customized built-ins still closes the panel instead of doing nothing. */
  const close = (details) => {
    if (details.getAttribute('aria-expanded') !== 'true') return;
    if (typeof details.toggle === 'function') {
      details.toggle(false);
    } else {
      details.removeAttribute('open');
      details.setAttribute('aria-expanded', 'false');
    }
  };

  const openClickMenu = (nav) => nav.querySelector(CLICK_LEVEL + '[aria-expanded="true"]');

  /* Header-wide, deliberately NOT scoped to one <nav>. Today there is exactly one, but the 2026-08-25 centred-logo
     experiment split the links into two <nav> halves either side of the logo, and if that layout comes back "is a
     menu open" has to keep meaning "in either half". Nothing costs anything by asking the document. */
  const openTopMenu = () => document.querySelector(ANY_TOP_LEVEL + '[aria-expanded="true"]');

  /* When each click menu was opened, so 1d can ignore moves while the panel is still animating open. */
  const openedAt = new WeakMap();

  const addClickBehaviour = (nav) => {
    if (nav.dataset.apdClickNav) return;
    nav.dataset.apdClickNav = '1';

    nav.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest('summary');
        if (!summary) return;

        const details = summary.parentElement;
        if (!details || !details.matches(ANY_TOP_LEVEL)) return; // deeper disclosures are not this handler's business

        const isClickMenu = details.matches(CLICK_LEVEL);

        if (isClickMenu && details.getAttribute('aria-expanded') === 'true') {
          /* (1) The SECOND click has to CLOSE, not navigate. theme.js's summary handler is
                   if (this.open && this.summaryElement.hasAttribute("data-follow-link"))
                     return (window.location.href = ...)
             and header.liquid puts data-follow-link on every nav summary. That listener is bound to the <summary>
             itself, so the only way to keep it out of the event without patching the shared bundle is to stop the
             click in the CAPTURE phase on the way down. */
          event.preventDefault();
          event.stopPropagation();
          close(details);
          return;
        }

        /* (2) Opening ANY top-level menu closes every other one. A click menu must never be left open UNDERNEATH a
           panel a later click opened: both panels are full-width and absolutely positioned, so two open at once
           means the lower one keeps hit-testing behind the visible one.
           theme.js will not do it: its click-outside guard is
             !this.contains(target) && !(target.closest("details") instanceof MenuDisclosure)
           and a click on another disclosure's summary fails the second test. theme.js does the opening itself. */
        nav.querySelectorAll(ANY_TOP_LEVEL).forEach((other) => {
          if (other !== details) close(other);
        });

        if (isClickMenu) openedAt.set(details, performance.now());
      },
      true
    );
  };

  /* --------------------------------------------------------------------------------------------------------------------
     1d. A CLICK MENU CLOSES ON A SECOND CLICK, OR WHEN THE POINTER LEAVES THE MENU AREA — AND THE NAV BAR ITSELF IS
         PART OF THAT AREA.
     --------------------------------------------------------------------------------------------------------------------
     Owner 2026-08-23, describing the Figma prototype:
       "in figma shop all and sale are closed only when clicked again on them or remove cursor below dropdown ...
        now in test store when i click on sale and move cursor to left to choose sale 15% it instantly closes because
        i go through collections and other items"

     Measured at 1440 before this change (diff_harness/nav_path.js): the Sale label sits at x=687 but its dropdown
     rows start at x=54, so the natural move to a row is LEFTWARDS along the nav row. The previous version of this
     file closed on `pointerleave` of the <li>, so the menu died at x=581, and the labels passed on the way
     (Signature Styles at x=433, New In at x=257) hover-opened their own panels over the top.

     Two rules replace that pointerleave:
       - the "menu area" is the whole header bar UNION the open panel, not one <li>. Crossing the other labels is
         therefore neutral ground: nothing closes, and nothing else opens either (1d-2 below).
       - only being outside that union closes it. Below the dropdown, or out to the side under the bar, is outside;
         anywhere in the bar or in the panel is inside.

     GRACE_MS ignores moves while the panel animates open — its rect grows from zero height, so a pointer already
     sitting where the panel is about to be would read as "outside" and close it one frame after the opening click.
     ------------------------------------------------------------------------------------------------------------------ */
  const SLOP = 6; // px tolerance, so a boundary pixel never flickers the panel shut
  const GRACE_MS = 400; // longer than the theme's open animation

  const inBox = (rect, x, y) =>
    rect.width > 0 &&
    rect.height > 0 &&
    x >= rect.left - SLOP &&
    x <= rect.right + SLOP &&
    y >= rect.top - SLOP &&
    y <= rect.bottom + SLOP;

  const insideMenuArea = (details, x, y) => {
    const item = details.parentElement;
    const bar = item.closest('.header') || item.closest('header') || item.parentElement;
    if (bar && inBox(bar.getBoundingClientRect(), x, y)) return true;
    if (inBox(item.getBoundingClientRect(), x, y)) return true;
    const panel = details.querySelector(':scope > .mega-menu, :scope > .header__dropdown-menu');
    return !!panel && inBox(panel.getBoundingClientRect(), x, y);
  };

  const addLeaveBehaviour = (nav) => {
    if (nav.dataset.apdLeaveArea) return;
    nav.dataset.apdLeaveArea = '1';

    let timer = null;

    const cancel = () => {
      if (timer === null) return;
      clearTimeout(timer);
      timer = null;
    };

    document.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType === 'touch' || !deviceCanHover()) return; // a tap must not close what it just opened

        const details = openTopMenu();
        if (!details) {
          cancel();
          return;
        }

        const opened = openedAt.get(details);
        if (opened !== undefined && performance.now() - opened < GRACE_MS) return;

        if (insideMenuArea(details, event.clientX, event.clientY)) {
          cancel();
          return;
        }

        if (timer !== null) return; // already counting down; a moving pointer must not keep restarting it
        timer = setTimeout(() => {
          timer = null;
          const still = openTopMenu();
          if (still) close(still);
        }, CLOSE_DELAY);
      },
      { passive: true }
    );
  };

  /* --------------------------------------------------------------------------------------------------------------------
     1d-2. WHILE A CLICK MENU IS OPEN, HOVERING ANOTHER LABEL DOES NOT OPEN THAT LABEL'S MENU.
     --------------------------------------------------------------------------------------------------------------------
     Without this, holding the click menu open across the nav row would be pointless: theme.js hover-opens Signature
     Styles / New In as the pointer passes them, their full-width panel paints over the open one, and the owner sees
     the same "it closed" symptom. theme.js binds its hover handler on the <details> itself,

         this.addEventListener("mouseover", <detectHover>)

     so a CAPTURE-phase listener on the nav runs first and stopPropagation() keeps the event from ever reaching it.
     Nothing is blocked while no click menu is open, and nothing is blocked inside the open menu's own subtree, so
     the hover menus behave exactly as before in every other state. Clicks are untouched: clicking New In still
     opens or follows New In, and rule (2) above closes the click menu at that moment.
     ------------------------------------------------------------------------------------------------------------------ */
  const addHoverSuppression = (nav) => {
    if (nav.dataset.apdHoverSuppress) return;
    nav.dataset.apdHoverSuppress = '1';

    nav.addEventListener(
      'mouseover',
      (event) => {
        const open = openClickMenu(nav);
        if (!open) return;
        const item = event.target.closest('.header__primary-nav-item');
        if (!item || item === open.parentElement) return; // inside the open click menu — leave it alone
        event.stopPropagation();
      },
      true
    );
  };
  /* --------------------------------------------------------------------------------------------------------------------
     1e. HOVER FOR THE HOVER MENUS ON DEVICES THE THEME GIVES UP ON.
     --------------------------------------------------------------------------------------------------------------------
     theme.js decides the trigger like this:

         get trigger() {
           return !window.matchMedia("screen and (pointer: fine)").matches ? "click" : this.getAttribute("trigger");
         }

     so it forces click-to-open on anything the browser does not call a FINE pointer — and `pointer: fine` is about
     precision, not about whether the device can hover. A touchscreen laptop, a pen tablet, or a machine where the
     coarse device happens to be the primary one all report `pointer: coarse` while `hover: hover` is perfectly true,
     and on those New In / Signature Styles would need a click.

     This adds hover on `(hover: hover)`, which is the query that actually means "this device can hover". It is a
     NO-OP whenever theme.js is already in hover mode — the guard returns early — so on an ordinary desktop nothing
     here runs and theme.js keeps sole control. `[data-open-on-click]` items are skipped throughout: hovering one
     still closes whatever was open, but never opens it.
     ------------------------------------------------------------------------------------------------------------------ */
  const themeHandlesHover = () => window.matchMedia('screen and (pointer: fine)').matches;

  const addHoverFallback = (nav) => {
    if (nav.dataset.apdHoverFallback) return;
    nav.dataset.apdHoverFallback = '1';

    let closeTimer;

    nav.addEventListener('pointerover', (event) => {
      if (themeHandlesHover() || !deviceCanHover()) return;
      if (event.pointerType === 'touch') return; // a tap must stay a tap
      if (openClickMenu(nav)) return; // 1d-2: an open click menu owns the nav row until it is closed

      const item = event.target.closest('.header__primary-nav-item');
      const details = item && item.querySelector(':scope > details.header__menu-disclosure');
      const hoverOpens = details && !item.matches(CLICK_ITEM);

      clearTimeout(closeTimer);

      nav.querySelectorAll(ANY_TOP_LEVEL).forEach((other) => {
        if (other !== details) close(other);
      });

      if (hoverOpens && details.getAttribute('aria-expanded') !== 'true') details.toggle(true);
    });

    nav.addEventListener('pointerleave', () => {
      if (themeHandlesHover() || !deviceCanHover()) return;
      if (openClickMenu(nav)) return; // 1d closes those by geometry, not by leaving the bar
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        nav.querySelectorAll(ANY_TOP_LEVEL).forEach(close);
      }, CLOSE_DELAY);
    });
  };

  /* --------------------------------------------------------------------------------------------------------------------
     1f. HOVER INTENT — AN OPEN MENU SURVIVES THE TRIP ACROSS THE NAV ROW.
     --------------------------------------------------------------------------------------------------------------------
     Owner 2026-08-25:
       "when sale drop down is opened and i want to move mouse left to choose option (they are far left) this option
        closes because i move mouse via other menu. so i have to first move down and then left. for first time users
        it may be irritating that menu always closes when you want to pick option."

     This is the classic diagonal-travel problem, and in this theme it has TWO causes, both in theme.js and both
     instant — no tolerance at all:

       detectHover_fn      on each <details>:  mouseover -> this.toggle(true)   ... opens the moment you touch a label
                                               mouseout  -> close after 250ms   ... so merely LEAVING Sale kills it
       detectHoverOutside_fn on document while open:  any mouseover whose closest("details") is another
                                               MenuDisclosure -> this.toggle(false), immediately

     So dragging the pointer left from Sale towards a row that starts at x=54 crosses Signature Styles / Shop All /
     New In, and the panel is gone before the pointer arrives. The panels are 1440 wide, so the target is ALWAYS to
     the side of the label that opened it — this is not a layout accident, it is every menu in the bar.

     The fix is intent, not geometry: while a menu is open, the rest of the header is NEUTRAL GROUND. Crossing it
     changes nothing, and another menu takes over only if the pointer actually SETTLES on its label for DWELL_MS.
     That is what Amazon-style mega menus do, and 300ms is below the threshold where a deliberate switch feels slow.

     How it is done without touching the shared theme.js: both offending listeners are reached by propagation — one
     is on the <details> (target phase), one is on document (bubble phase) — so a CAPTURE listener on the header
     runs before either and stopPropagation() ends the trip. Nothing is patched, unbound or overridden.

     Consequences, deliberate:
       - theme.js no longer closes hover menus at all, so 1d above is what closes them: the pointer leaving the bar
         UNION the open panel. That rule now applies to hover menus, not just click menus.
       - while a menu is open, mouseover inside the header is stopped for everything else in the header too. The
         window is short (only while open) and nothing else in this header opens on hover, but it is worth knowing.
       - the dwell switch is re-checked against elementFromPoint when it fires, so a pointer that has moved on —
         downwards into the panel, or further along the row — never triggers a switch it has left behind.
     ------------------------------------------------------------------------------------------------------------------ */
  /* Calibrated on the render, not guessed. Owner tried 300 and said it "feels too long to wait until dropdown
     opens", so it is 150 — the delay you feel when you deliberately move to another label and stop.
     ⚠ These two numbers are NOT interchangeable knobs. SETTLE_PX does the protecting: any pointermove more than
     6px from where the countdown started restarts it, so a pointer that is still travelling never reaches
     DWELL_MS on a label it is only passing. DWELL_MS is therefore only "how long after you STOP", which is why
     it can be this short. Measured with a real pointer driven at fixed speeds along the row
     (diff_harness/nav_speed.js): an open menu survives a drag across the whole bar at 2000 / 1000 / 600 / 400 /
     250 / 150 / 100 px/s, and gives way at 60 px/s — a 14-second crawl across the bar, which is a hover, not a
     traverse. Raising DWELL_MS does not widen that band; lowering SETTLE_PX does. */
  const DWELL_MS = 150;
  const SETTLE_PX = 6;

  const addHoverIntent = (header) => {
    if (header.dataset.apdHoverIntent) return;
    header.dataset.apdHoverIntent = '1';

    let dwellTimer = null;
    let dwellFor = null;
    let anchor = { x: -1, y: -1 };
    let pointer = { x: -1, y: -1 };

    const cancelDwell = () => {
      if (dwellTimer !== null) clearTimeout(dwellTimer);
      dwellTimer = null;
      dwellFor = null;
    };

    const insideOpenMenu = (open, node) => !!node && open.parentElement.contains(node);

    /* ---- the suppression half: while a menu is open, the rest of the header is neutral ground ---------------- */
    const guard = (event) => {
      if (!deviceCanHover()) return;

      const open = openTopMenu();
      if (!open) return;

      if (event.type === 'mouseout') {
        /* theme.js starts a 250ms close the moment the pointer leaves the <details> — which is exactly what
           happens when you set off towards the panel's far-left rows. As long as the pointer is going SOMEWHERE
           ELSE IN THE HEADER, that event must not reach it. A mouseout whose relatedTarget is outside the header
           is a genuine exit and is left alone, so theme.js and 1d close the menu together. relatedTarget is also
           null when the pointer leaves the WINDOW, and that counts as an exit for the same reason — otherwise a
           menu would still be sitting open when the user came back, with no pointermove to close it. */
        const to = event.relatedTarget;
        if (to && header.contains(to)) event.stopPropagation();
        return;
      }

      /* Inside the open menu — its own label or its panel. theme.js is right about this one, leave it alone. */
      if (insideOpenMenu(open, event.target)) return;

      /* Any other part of the header. Stopping the mouseover here blanks BOTH of theme.js's openers: the
         <details>'s own mouseover (target phase) and the document-level detectHoverOutside (bubble phase). */
      event.stopPropagation();
    };

    header.addEventListener('mouseover', guard, true);
    header.addEventListener('mouseout', guard, true);

    /* ---- the intent half: another menu takes over only once the pointer has SETTLED on its label ------------- */
    document.addEventListener(
      'pointermove',
      (event) => {
        pointer = { x: event.clientX, y: event.clientY };
        if (event.pointerType === 'touch' || !deviceCanHover()) return;

        const open = openTopMenu();
        if (!open) {
          cancelDwell();
          return;
        }

        if (insideOpenMenu(open, event.target)) {
          cancelDwell();
          return;
        }

        const item = event.target.closest && event.target.closest('.header__primary-nav-item');
        if (!item || item === open.parentElement) {
          cancelDwell(); // the logo, the icons, the gaps — nothing has been chosen, so nothing changes
          return;
        }

        /* The dwell target is the ITEM, not its menu, so settling on Collections or Our Story — top-level links
           with no panel — closes what is open instead of leaving a panel hanging over the page while the pointer
           sits somewhere else in the row. Crossing them still does nothing. */
        const details = item.querySelector(':scope > details.header__menu-disclosure');

        /* Still moving? Then nothing has been chosen: push the countdown back to the new position. A pointer
           crossing the row never stops moving, so it never reaches DWELL_MS on any label it passes; one that
           comes to rest on a label fires it. */
        const travelled = Math.abs(pointer.x - anchor.x) + Math.abs(pointer.y - anchor.y);
        if (dwellFor === item && travelled <= SETTLE_PX) return;

        cancelDwell();
        dwellFor = item;
        anchor = { x: pointer.x, y: pointer.y };
        dwellTimer = setTimeout(() => {
          dwellTimer = null;
          dwellFor = null;
          const under = document.elementFromPoint(pointer.x, pointer.y);
          if (!under || !item.contains(under)) return; // the pointer has moved on; this switch is stale
          const stillOpen = openTopMenu();
          if (stillOpen === details) return;
          if (stillOpen) close(stillOpen);
          if (details && typeof details.toggle === 'function') details.toggle(true);
        }, DWELL_MS);
      },
      { passive: true }
    );
  };

  const init = () => {
    document
      .querySelectorAll('.header__primary-nav-item > details.header__menu-disclosure')
      .forEach(watch);
    document.querySelectorAll('.header__primary-nav').forEach((nav) => {
      addClickBehaviour(nav);
      addHoverSuppression(nav);
      addHoverFallback(nav);
    });
    /* Header-scoped, not nav-scoped: both of these reason about the header as ONE area (the bar union the open
       panel), which is not a property of any single <nav>. It also keeps them correct if the links are ever split
       into two navs again — binding per nav would give the document two pointermove listeners and let each half
       ignore a menu open in the other. */
    document.querySelectorAll('x-header.header').forEach((header) => {
      addLeaveBehaviour(header);
      addHoverIntent(header);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* --------------------------------------------------------------------------------------------------------------------
     2. A THIRD level in the mobile drawer.
     --------------------------------------------------------------------------------------------------------------------
     Figma drills main -> "Shop" -> "Swimwear" -> the products (frames 2050:2933 -> 2050:3088 -> 2050:3098), and the
     designer's annotation on the mobile frame asks for a chevron per row plus an arrow-and-"Back" to come back. The
     theme ships two levels and renders the third as an in-place accordion with an animated PLUS.

     theme.js is a shared file no lane owns, so nothing here patches it. It does not need patching: its
     switchPanel(fromPanel, toPanel) already switches between ANY two `#id`s inside the single
     <header-sidebar-collapsible-panel>, driven by that element's aria-activedescendant. So every panel — level 2
     and level 3 alike — is rendered inside it by snippets/header-sidebar.liquid, and all this has to do is set
     aria-activedescendant for the deeper rows and point the deeper Back button back at its parent panel.

     Level-1 rows keep the theme's own aria-controls handler, so opening the first level is untouched; only the
     rows this file marks with data-drill-to are handled here.
     ------------------------------------------------------------------------------------------------------------------ */
  const drill = (event) => {
    const trigger = event.target.closest('[data-drill-to]');
    if (!trigger) return;

    const sidebar = trigger.closest('header-sidebar');
    const collapsible = sidebar && sidebar.querySelector('header-sidebar-collapsible-panel');
    const target = trigger.getAttribute('data-drill-to');
    if (!collapsible || !target || !collapsible.querySelector('#' + target)) return;

    event.preventDefault();

    /* Same bookkeeping the theme does for level 1, so the "siblings fade" rules in theme.css still read right. */
    trigger
      .closest('.header-sidebar__linklist')
      ?.querySelectorAll('[data-drill-to]')
      .forEach((button) => button.setAttribute('aria-expanded', String(button === trigger)));

    collapsible.setAttribute('aria-activedescendant', target);
  };

  document.addEventListener('click', drill);

  /* Section re-render in the theme editor replaces the header markup wholesale. */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.querySelector('.header__primary-nav-item')) init();
  });
})();
